import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { PrimaryButton, RegularButton } from "@components";

import {
  CustomerRefInput,
  CustomerType,
  SearchProfile_Submit,
  SearchProfiles_Search
} from "@graphql";
import { store } from "@store";
import { SearchProfileFormInput } from "./SearchProfileFormInput";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";

type Props = {
  open: boolean;
  getInput: () => SearchProfileFormInput;
  onClose: () => void;
  hideCustomer?: boolean;
  id?: string;
};

const AddSearchProfileDialog: React.FC<Props> = ({
  onClose,
  open,
  getInput,
  hideCustomer = false,
  id = null
}) => {
  const [name, setName] = useState("");
  const [customerRefs, setCustomerRefs] = useState<CustomerRefInput[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const input = getInput();
    setName(input?.name);

    setCustomerRefs(input?.customerRefs ?? []);
  }, [getInput]);

  const submit = (): void => {
    const input = getInput();
    setLoading(true);
    input.name = name;

    input.customerRefs = customerRefs;
    const data = input.getPlain();
    SearchProfile_Submit.func(
      { data, id },
      {
        refetchQueries: [SearchProfiles_Search.OperationName]
      }
    )
      .then(() => {
        store().snackbars.success(locale.Saved);
        onClose();
      })
      .catch((e) => {
        store().snackbars.error(`Error: ${e}`);
      })
      .finally(() => {
        setLoading(false);
        setName("");
        setCustomerRefs([]);
      });
  };

  const canSubmit = !!name && !loading;

  return (
    <Dialog
      fullWidth
      maxWidth={"sm"}
      open={open}
      onClose={onClose}
      TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
    >
      <DialogTitle>{locale.SearchProfiles}</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          label={locale.Title}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          multiline
        />
        {!hideCustomer && (
          <CustomerSelector
            fullWidth
            slim
            multi
            hideButton
            onChange={(v) =>
              setCustomerRefs(v?.map((x) => ({ Id: x._id, Name: x.Name, Type: x.Type })))
            }
            value={customerRefs?.map((x) => ({ label: x.Name, value: x.Id }))}
            initialFilter={{
              customerType: [CustomerType.Hospital, CustomerType.Department]
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <RegularButton variant="text" onClick={onClose}>
          {locale.No}
        </RegularButton>

        <PrimaryButton variant="text" disabled={!canSubmit} onClick={submit}>
          {locale.Save}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddSearchProfileDialog;
